import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import { useResourceUtilizationKPI } from "./hooks/useResourceUtilizationKPI";
import ResourceUtilizationKPISettings from "./ResourceUtilizationKPISettings";
import * as Tooltip from "@radix-ui/react-tooltip";
import DateRangePresets from "../../filter/TabFilterModal/DateRangePresets";
import { useAutoFitFontSize } from "../_shared/useAutoFitFontSize";

const UTILIZATION_KPI_VALUE_MAX_PX = 36; // was text-4xl
const UTILIZATION_KPI_VALUE_MIN_PX = 16;

// No compact fallback: a percentage/duration display string, not a raw large count.
const UtilizationKPIValue = ({ text }) => {
  const { ref, fontSizePx } = useAutoFitFontSize({
    text,
    maxPx: UTILIZATION_KPI_VALUE_MAX_PX,
    minPx: UTILIZATION_KPI_VALUE_MIN_PX,
  });

  return (
    <div ref={ref} className="max-w-full truncate font-bold text-primary-600 tracking-tight" style={{ fontSize: `${fontSizePx}px` }}>
      {text}
    </div>
  );
};

const GRANULARITY_LABELS = {
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
};

function formatGermanDateLabel(value) {
  const parsed = new Date(value + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Default anchor date per granularity. */
function defaultAnchor(gran) {
  const today = new Date();
  if (gran === "day") {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    return y.toISOString().slice(0, 10);
  }
  return today.toISOString().slice(0, 10);
}

/** Compute a display-only date range from selectedDate + granularity. */
function computeDisplayRange(dateStr, gran) {
  if (!dateStr) return { startDate: "", endDate: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { startDate: "", endDate: "" };
  const fmt = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

  if (gran === "day") return { startDate: fmt(d), endDate: fmt(d) };

  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmt(mon), endDate: fmt(sun) };
  }

  if (gran === "month") {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { startDate: fmt(start), endDate: fmt(end) };
  }

  // year
  return {
    startDate: `${d.getFullYear()}-01-01`,
    endDate: `${d.getFullYear()}-12-31`,
  };
}

/** Format seconds into the most appropriate unit. */
function formatDuration(seconds) {
  const s = Number(seconds);
  if (s < 60) return `${s.toFixed(0)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)} min`;
  if (s < 86400) return `${(s / 3600).toFixed(2)} hrs`;
  return `${(s / 86400).toFixed(2)} days`;
}

/** Isolated date input — uncontrolled to prevent Chrome's date-section cursor from
 *  resetting on every React re-render.
 *
 *  Chrome fires native 'change' on each section auto-advance (month→day→year) during
 *  keyboard typing, NOT only when the picker closes. We distinguish the two paths via
 *  a pointer flag: if the last interaction was a pointer event (click = calendar picker),
 *  commit on 'change'; if it was a key event (keyboard typing), defer to blur / Enter. */
const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  const committed = useRef(value);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const isPointerRef = useRef(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Sync from parent when value changes externally (e.g. granularity button click)
  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value;
    committed.current = value;
  }, [value]);

  const commit = useCallback(() => {
    const val = inputRef.current?.value;
    if (!val || val === committed.current) return;
    // Reject if not a valid YYYY-MM-DD date (e.g. partially-typed year like "0020-…")
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val) || isNaN(new Date(val + "T00:00:00").getTime())) return;
    committed.current = val;
    onChangeRef.current(val);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onPointerDown = () => { isPointerRef.current = true; };
    const onKeyDown = () => { isPointerRef.current = false; };
    // Only commit from 'change' if triggered by a pointer interaction (calendar picker).
    // Keyboard-driven section auto-advance also fires 'change' — those are deferred to blur/Enter.
    const onNativeChange = () => {
      if (isPointerRef.current) {
        isPointerRef.current = false;
        commit();
      }
    };
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('change', onNativeChange);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('change', onNativeChange);
    };
  }, [commit]);

  return (
    <input
      ref={inputRef}
      type="date"
      defaultValue={value}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      onBlur={commit}
      disabled={disabled}
      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
    />
  );
});

function ResourceUtilizationKPI({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const { t } = useTranslation();
  const {
    loading,
    error,
    result,
    datasetLastDate,
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    enabledGranularities,
    // ViewState
    viewResources,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setConfiguredResources,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setEnabledGranularities,
    // ViewState setters
    setViewResources,
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    // Actions
    fetchMetadata,
    fetchAvailability,
  } = useResourceUtilizationKPI(activeFile, initialSettings);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, selectedObjectType, cycleStartActivity, cycleEndActivity, configuredResources,
      shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNonWorkingDays, includeNoDataResources, multiResourceMode, enabledGranularities,
      viewResources, granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd,
    }));
  }, [title, selectedObjectType, cycleStartActivity, cycleEndActivity, // eslint-disable-line react-hooks/exhaustive-deps
      configuredResources, shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNonWorkingDays, includeNoDataResources, multiResourceMode, enabledGranularities,
      viewResources, granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd]);

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Resource Utilization");
  }, [title]);

  const { filterState } = useGlobalFilters();
  const isFirstFilterRenderRef = useRef(true);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [viewResourceError, setViewResourceError] = useState("");
  const [viewDateError, setViewDateError] = useState("");
  const [shownPeriodLabel, setShownPeriodLabel] = useState(null);
  const viewResourcesRef = useRef(viewResources);
  const resourceSelectionDirtyRef = useRef(false);

  useEffect(() => {
    viewResourcesRef.current = viewResources;
  }, [viewResources]);

  // Derived display range — purely for showing period label before API responds
  const displayRange = useMemo(
    () => computeDisplayRange(selectedDate, granularity),
    [selectedDate, granularity],
  );

  // Update the granularity period label only when a new result arrives.
  // Intentionally omits viewMode/displayRange from deps — mode switches alone must not update the label.
  // In date range mode the label is set when Apply is clicked (see handleApplyDateRange).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!result || viewMode !== "granularity") return;
    setShownPeriodLabel(
      `${formatGermanDateLabel(displayRange.startDate)} – ${formatGermanDateLabel(displayRange.endDate)}`,
    );
  }, [result]);

  // --- Settings modal handlers (Edit Mode) ---

  const handleOpenSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);

  const handleApplySettings = useCallback(async (values) => {
    // If current granularity is no longer enabled, reset to first enabled
    const normalizedGranularity = values.enabledGranularities.includes(granularity)
      ? granularity
      : values.enabledGranularities[0] || "day";

    // Keep viewResources that are still in the new configuredResources
    const nextViewResources =
      values.configuredResources.length > 0
        ? (() => {
            const kept = viewResources.filter((r) =>
              values.configuredResources.includes(r),
            );
            return kept.length > 0 ? kept : values.configuredResources;
          })()
        : [];

    if (onTitleChange) onTitleChange(values.title);

    // Commit TileConfig
    setTitle(values.title);
    setSelectedObjectType(values.selectedObjectType);
    setConfiguredResources(values.configuredResources);
    setShiftStart(values.shiftStart);
    setShiftEnd(values.shiftEnd);
    setBreaks(values.breaks);
    setToleranceThreshold(values.toleranceThreshold);
    setIgnoreShortTransitions(values.ignoreShortTransitions);
    setIncludeNonWorkingDays(values.includeNonWorkingDays);
    setIncludeNoDataResources(values.includeNoDataResources);
    setMultiResourceMode(values.multiResourceMode);
    setEnabledGranularities(values.enabledGranularities);

    // Update ViewState as needed
    setViewResources(nextViewResources);
    setGranularity(normalizedGranularity);

    await fetchAvailability({
      ...values,
      granularity: normalizedGranularity,
      viewResources: nextViewResources,
      selectedDate,
    });
  }, [
    fetchAvailability,
    granularity,
    selectedDate,
    onTitleChange,
    viewResources,
    setBreaks,
    setConfiguredResources,
    setEnabledGranularities,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setSelectedObjectType,
    setShiftEnd,
    setShiftStart,
    setTitle,
    setToleranceThreshold,
    setGranularity,
    setViewResources,
  ]);

  // --- Re-fetch when global filters change ---

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    fetchMetadata();
    if (configuredResources.length > 0 && selectedObjectType && viewResources.length > 0) {
      fetchAvailability();
    }
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Auto-load & auto-fetch ---

  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(async () => {
        if (isMetadataLoaded) await fetchAvailability();
      });
    }
  }, [onRefresh, isMetadataLoaded, fetchAvailability]);

  // --- Header ---

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    ),
    [handleOpenSettings],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // --- Computed display values ---

  const availabilityDisplay = useMemo(() => {
    if (!result) return "0.00%";
    return `${Number(result.availability_percentage || 0).toFixed(2)}%`;
  }, [result]);

  const handleViewResourcesChange = useCallback((nextResources) => {
    resourceSelectionDirtyRef.current = true;
    viewResourcesRef.current = nextResources;
    setViewResources(nextResources);
    setViewResourceError(
      nextResources.length > 0 ? "" : "Select at least one resource.",
    );
  }, [setViewResources]);

  const triggerAutoApply = useCallback(
    (overrides = {}) => {
      const effectiveViewResources = overrides.viewResources ?? viewResourcesRef.current;
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart = overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (!effectiveViewResources || effectiveViewResources.length === 0) {
        setViewResourceError("Select at least one resource.");
        return false;
      }
      setViewResourceError("");

      if (effectiveViewMode === "daterange") {
        if (!effectiveDateRangeStart || !effectiveDateRangeEnd) {
          setViewDateError("Select start and end dates.");
          return false;
        }
        if (effectiveDateRangeStart > effectiveDateRangeEnd) {
          setViewDateError("Start date must be on or before end date.");
          return false;
        }
      }
      setViewDateError("");

      fetchAvailability({
        viewResources: effectiveViewResources,
        viewMode: effectiveViewMode,
        dateRangeStart: effectiveDateRangeStart,
        dateRangeEnd: effectiveDateRangeEnd,
        ...overrides,
      });
      return true;
    },
    [fetchAvailability, viewMode, dateRangeStart, dateRangeEnd],
  );

  const handleResourceDropdownOpenChange = useCallback(
    (isOpen) => {
      if (isOpen || !resourceSelectionDirtyRef.current) return;
      resourceSelectionDirtyRef.current = false;
      if (viewMode !== "granularity") return;
      if (viewResourcesRef.current.length === 0) return;
      setViewResourceError("");
      triggerAutoApply({ viewResources: viewResourcesRef.current });
    },
    [viewMode, triggerAutoApply],
  );

  const handleGranularityChange = useCallback(
    (g) => {
      setGranularity(g);
      const baseAnchor = selectedDate || defaultAnchor(g);
      const normalizedAnchor =
        datasetLastDate && baseAnchor > datasetLastDate
          ? datasetLastDate
          : baseAnchor;
      setSelectedDate(normalizedAnchor);
      triggerAutoApply({ granularity: g, selectedDate: normalizedAnchor });
    },
    [setGranularity, setSelectedDate, selectedDate, datasetLastDate, triggerAutoApply],
  );

  const handleViewModeChange = useCallback(
    (nextMode) => {
      if (nextMode === viewMode) return;
      if (nextMode !== "daterange") {
        const baseAnchor = dateRangeStart || selectedDate || defaultAnchor(granularity);
        const normalizedAnchor =
          datasetLastDate && baseAnchor > datasetLastDate
            ? datasetLastDate
            : baseAnchor;
        setSelectedDate(normalizedAnchor);
      }
      // Switching to daterange: leave dateRangeStart/End as their own independent state
      setViewMode(nextMode);
    },
    [
      viewMode,
      selectedDate,
      granularity,
      dateRangeStart,
      datasetLastDate,
      setSelectedDate,
      setViewMode,
    ],
  );

  const handleSelectedDateCommit = useCallback(
    (nextDate) => {
      setSelectedDate(nextDate);
      if (viewMode === "granularity") {
        triggerAutoApply({ viewMode: "granularity", selectedDate: nextDate });
      }
    },
    [setSelectedDate, viewMode, triggerAutoApply],
  );

  const handleDateRangeStartCommit = useCallback(
    (nextDate) => {
      setDateRangeStart(nextDate);
      setViewDateError("");
    },
    [setDateRangeStart],
  );

  const handleDateRangeEndCommit = useCallback(
    (nextDate) => {
      setDateRangeEnd(nextDate);
      setViewDateError("");
    },
    [setDateRangeEnd],
  );

  const handleApplyDateRange = useCallback(() => {
    setShownPeriodLabel(
      `${formatGermanDateLabel(dateRangeStart)} – ${formatGermanDateLabel(dateRangeEnd)}`,
    );
    triggerAutoApply({ viewMode: "daterange" });
  }, [triggerAutoApply, dateRangeStart, dateRangeEnd]);

  const handleDateRangePresetChange = useCallback(
    (value) => {
      const start = (value?.startDate || "").slice(0, 10);
      const end = (value?.endDate || "").slice(0, 10);
      setDateRangeStart(start);
      setDateRangeEnd(end);
      setViewDateError("");
      setShownPeriodLabel(`${formatGermanDateLabel(start)} – ${formatGermanDateLabel(end)}`);
      triggerAutoApply({ viewMode: "daterange", dateRangeStart: start, dateRangeEnd: end });
    },
    [setDateRangeStart, setDateRangeEnd, triggerAutoApply],
  );

  const canApplyView = viewResources.length > 0;

  const showConfigPrompt =
    isMetadataLoaded && !loading && !error && configuredResources.length === 0;
  const showKpi = isMetadataLoaded && !error && configuredResources.length > 0;

  return (
    <BaseTile title={title} loading={loading} minHeight="sm" className="h-full">
      <TileContent layout="flex" direction="column" gap="md" grow>
        {showConfigPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Utilization KPI"
            message="Open settings to configure resources, shift, and enabled granularities."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Configure KPI
              </button>
            }
          />
        )}

        {showKpi && (
          <>
            {/* Controls card */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              {/* Row 1: Resources + view mode toggle */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="sm:w-96">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Resources
                  </label>
                  <MultiSelectDropdown
                    options={configuredResources}
                    selectedValues={viewResources}
                    onSelectionChange={handleViewResourcesChange}
                    onOpenChange={handleResourceDropdownOpenChange}
                    placeholder="Select resources..."
                    itemType="resources"
                    className="text-sm w-full"
                    minOptionWidth="0"
                    explicitDisplayCount={5}
                  />
                  {(viewResourceError || !canApplyView) && (
                    <p className="mt-1 text-xs text-error-600">
                      {viewResourceError || "Select at least one resource."}
                    </p>
                  )}
                </div>
                <div className="flex gap-0.5 bg-gray-200 rounded-md p-0.5 self-start">
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("granularity")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "granularity"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Granularity
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("daterange")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "daterange"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Date Range
                  </button>
                </div>
              </div>

              {/* Row 2: Time Period label + tooltip */}
              <div className="flex items-center gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Time Period
                </label>
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        className="inline-flex text-gray-400 hover:text-gray-500 transition-colors"
                        aria-label="Time period help"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="top"
                        align="start"
                        sideOffset={6}
                        className="max-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
                      >
                        <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
                          <span className="text-xs font-medium text-primary-700">
                            {viewMode === "granularity"
                              ? "Granularity Mode"
                              : "Date Range Mode"}
                          </span>
                        </div>
                        <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
                          {viewMode === "granularity" ? (
                            <>
                              Pick a granularity (
                              <span className="text-primary-600 font-medium">
                                Day, Week, Month, Year
                              </span>
                              ) and select a date. Results are calculated for
                              the full calendar period (Week = calendar week).
                            </>
                          ) : (
                            <>
                              Select an explicit{" "}
                              <span className="text-primary-600 font-medium">
                                start
                              </span>{" "}
                              and{" "}
                              <span className="text-primary-600 font-medium">
                                end date
                              </span>
                              . The KPI covers that exact range.
                            </>
                          )}
                        </div>
                        <Tooltip.Arrow className="fill-white" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </div>

              {/* Row 3: granularity buttons / date range inputs */}
              {viewMode === "granularity" ? (
                <div className="flex flex-wrap items-center gap-2">
                  {enabledGranularities.map((g) => {
                    const active = granularity === g;
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => handleGranularityChange(g)}
                        className={`px-3 py-1.5 rounded-md border text-sm ${
                          active
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        {GRANULARITY_LABELS[g] || g}
                      </button>
                    );
                  })}
                  <div className="flex items-center gap-3">
                    <DateInput
                      value={selectedDate}
                      onChange={handleSelectedDateCommit}
                      disabled={loading}
                    />
                    <span className="text-xs text-gray-500">
                      {result?.period_label ||
                        `${formatGermanDateLabel(displayRange.startDate)} - ${formatGermanDateLabel(displayRange.endDate)}`}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <DateInput
                      value={dateRangeStart}
                      onChange={handleDateRangeStartCommit}
                      disabled={loading}
                    />
                    <span className="text-xs text-gray-500">to</span>
                    <DateInput
                      value={dateRangeEnd}
                      onChange={handleDateRangeEndCommit}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={handleApplyDateRange}
                      disabled={loading || viewResources.length === 0}
                      className="px-4 py-1.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? "Loading..." : "Apply"}
                    </button>
                  </div>
                  {viewDateError && (
                    <p className="text-xs text-error-600">{viewDateError}</p>
                  )}
                  <DateRangePresets
                    value={{ startDate: dateRangeStart || null, endDate: dateRangeEnd || null, enabled: true }}
                    onChange={handleDateRangePresetChange}
                  />
                </div>
              )}
            </div>

            {/* KPI display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center flex-1 flex flex-col items-center justify-center min-h-[120px]">
              {result && result.total_shift_duration_seconds === 0 ? (
                <>
                  <div className="text-sm font-semibold text-gray-500 mb-1">Utilization</div>
                  <div className="text-base font-medium text-gray-400 italic">
                    No cycle data for selected period
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-gray-800 mb-1">Utilization</div>
                  <UtilizationKPIValue text={availabilityDisplay} />
                  {result && (
                    <>
                      <div className="text-xs text-gray-500 mt-2">
                        Downtime: {formatDuration(result.total_downtime_seconds || 0)}
                      </div>
                      {shownPeriodLabel && (
                        <div className="text-xs text-gray-800 mt-1">
                          {shownPeriodLabel}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load KPI"
            message={typeof error === "string" ? error : "Unexpected error."}
            action={
              <button
                onClick={fetchAvailability}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Retry
              </button>
            }
          />
        )}
      </TileContent>

      {/* Edit Mode: Settings modal — persistent config only */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="Resource Utilization KPI Settings"
        getInitialValues={() => ({
          title,
          selectedObjectType,
          configuredResources: [...configuredResources],
          shiftStart,
          shiftEnd,
          breaks: breaks.map((b) => ({ ...b })),
          toleranceThreshold,
          ignoreShortTransitions,
          includeNonWorkingDays,
          includeNoDataResources,
          multiResourceMode,
          enabledGranularities: [...enabledGranularities],
        })}
        onApply={handleApplySettings}
        isApplyDisabled={(values) =>
          !(
            isMetadataLoaded &&
            values.selectedObjectType &&
            values.configuredResources?.length > 0 &&
            selectedDate &&
            values.enabledGranularities?.length > 0
          ) || loading
        }
        applyButtonText={loading ? "Calculating..." : "Apply and Calculate"}
      >
        {({ values, setField, update }) => (
          <ResourceUtilizationKPISettings
            title={values.title}
            objectTypes={objectTypes}
            selectedObjectType={values.selectedObjectType}
            availableResources={availableResources}
            configuredResources={values.configuredResources}
            shiftStart={values.shiftStart}
            shiftEnd={values.shiftEnd}
            breaks={values.breaks}
            toleranceThreshold={values.toleranceThreshold}
            ignoreShortTransitions={values.ignoreShortTransitions}
            includeNonWorkingDays={values.includeNonWorkingDays}
            includeNoDataResources={values.includeNoDataResources}
            multiResourceMode={values.multiResourceMode}
            enabledGranularities={values.enabledGranularities}
            onTitleChange={(v) => setField("title", v)}
            onObjectTypeChange={(v) => setField("selectedObjectType", v)}
            onConfiguredResourcesChange={(v) => setField("configuredResources", v)}
            onShiftStartChange={(v) => setField("shiftStart", v)}
            onShiftEndChange={(v) => setField("shiftEnd", v)}
            onAddBreak={() =>
              update((p) => ({
                ...p,
                breaks: [...p.breaks, { start: "12:00", end: "12:30" }],
              }))
            }
            onRemoveBreak={(idx) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.filter((_, i) => i !== idx),
              }))
            }
            onUpdateBreak={(idx, field, val) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.map((b, i) =>
                  i === idx ? { ...b, [field]: val } : b,
                ),
              }))
            }
            onToleranceThresholdChange={(v) => setField("toleranceThreshold", v)}
            onIgnoreShortTransitionsChange={(v) =>
              setField("ignoreShortTransitions", v)
            }
            onIncludeNonWorkingDaysChange={(v) =>
              setField("includeNonWorkingDays", v)
            }
            onIncludeNoDataResourcesChange={(v) =>
              setField("includeNoDataResources", v)
            }
            onMultiResourceModeChange={(v) => setField("multiResourceMode", v)}
            onEnabledGranularitiesChange={(v) =>
              setField("enabledGranularities", v)
            }
            loading={loading}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
}

export default ResourceUtilizationKPI;
